import React, { useCallback, useEffect, useState } from 'react';
import { FiCopy } from 'react-icons/fi';
import api from '../services/api';
import TotpSetupQrBlock from './TotpSetupQrBlock';

async function copyTextToClipboard(text) {
  const t = String(text || '');
  if (!t) return false;
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      /* fallback */
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Для любого входа с валидным JWT: если 2FA не включена в БД — блокируем приложение
 * до настройки TOTP (десктоп и мобильная версия). После включения — обычная работа
 * до истечения сессии; при новом входе без 2FA снова показывается экран.
 */
export default function Mandatory2faGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [twoFaOk, setTwoFaOk] = useState(false);
  const [error, setError] = useState('');
  const [copyHint, setCopyHint] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupOtpauth, setSetupOtpauth] = useState('');
  const [enableCode, setEnableCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  const fetchTwofaPolicyRequired = useCallback(async () => {
    try {
      const r = await fetch('/api/registration-settings');
      const data = await r.json().catch(() => ({}));
      if (data.twofaRequired === false) return false;
      return true;
    } catch {
      return true;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('/api/me/2fa/status');
      const ok = !!(data?.enabled && data?.hasSecret);
      setTwoFaOk(ok);
      return ok;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Не удалось проверить 2FA');
      setTwoFaOk(false);
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const policyOn = await fetchTwofaPolicyRequired();
      if (cancelled) return;
      if (!policyOn) {
        setTwoFaOk(true);
        setLoading(false);
        return;
      }
      const ok = await fetchStatus();
      if (cancelled) return;
      setLoading(false);
      if (!ok) {
        setSetupSecret('');
        setSetupOtpauth('');
        setEnableCode('');
        setSetupLoading(true);
        try {
          const { data } = await api.post('/api/me/2fa/setup');
          if (!cancelled) {
            setSetupSecret(data.secret || '');
            setSetupOtpauth(data.otpauthUrl || '');
          }
        } catch (e) {
          if (!cancelled) {
            setError(e.response?.data?.error || e.message || 'Не удалось получить ключ для настройки');
          }
        } finally {
          if (!cancelled) setSetupLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchStatus, fetchTwofaPolicyRequired]);

  useEffect(() => {
    const on2faChanged = () => {
      setLoading(true);
      fetchTwofaPolicyRequired().then((policyOn) => {
        if (!policyOn) {
          setTwoFaOk(true);
          setLoading(false);
          return;
        }
        fetchStatus().then((ok) => {
          setLoading(false);
          if (ok) {
            setSetupSecret('');
            setSetupOtpauth('');
            setEnableCode('');
            return;
          }
          setSetupLoading(true);
          api
            .post('/api/me/2fa/setup')
            .then(({ data }) => {
              setSetupSecret(data.secret || '');
              setSetupOtpauth(data.otpauthUrl || '');
            })
            .catch((e) => {
              setError(e.response?.data?.error || e.message || 'Ошибка');
            })
            .finally(() => setSetupLoading(false));
        });
      });
    };
    window.addEventListener('mesendger:2fa-changed', on2faChanged);
    return () => window.removeEventListener('mesendger:2fa-changed', on2faChanged);
  }, [fetchStatus, fetchTwofaPolicyRequired]);

  useEffect(() => {
    const onPolicy = () => {
      setLoading(true);
      fetchTwofaPolicyRequired().then((policyOn) => {
        if (!policyOn) {
          setTwoFaOk(true);
          setLoading(false);
          setError('');
          return;
        }
        fetchStatus().then((ok) => {
          setTwoFaOk(ok);
          setLoading(false);
          if (!ok) {
            setSetupLoading(true);
            api
              .post('/api/me/2fa/setup')
              .then(({ data }) => {
                setSetupSecret(data.secret || '');
                setSetupOtpauth(data.otpauthUrl || '');
              })
              .catch((e) => {
                setError(e.response?.data?.error || e.message || 'Ошибка');
              })
              .finally(() => setSetupLoading(false));
          } else {
            setSetupSecret('');
            setSetupOtpauth('');
            setEnableCode('');
          }
        });
      });
    };
    window.addEventListener('mesendger:2fa-policy-changed', onPolicy);
    return () => window.removeEventListener('mesendger:2fa-policy-changed', onPolicy);
  }, [fetchStatus, fetchTwofaPolicyRequired]);

  const copySecret = useCallback(async () => {
    setError('');
    setCopyHint('');
    const ok = await copyTextToClipboard(setupSecret);
    if (ok) {
      setCopyHint('Секрет скопирован — вставьте в приложение-аутентификатор');
      window.setTimeout(() => setCopyHint(''), 3500);
    } else {
      setError('Не удалось скопировать автоматически. Выделите ключ вручную.');
    }
  }, [setupSecret]);

  const submitEnable = async (e) => {
    e.preventDefault();
    setError('');
    if (!setupSecret || !enableCode.trim()) {
      setError('Введите код из приложения');
      return;
    }
    setSetupLoading(true);
    try {
      await api.post('/api/me/2fa/enable', {
        secret: setupSecret,
        code: enableCode.replace(/\s/g, ''),
      });
      setTwoFaOk(true);
      setSetupSecret('');
      setSetupOtpauth('');
      setEnableCode('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Ошибка');
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading && !twoFaOk) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(160deg, #0f172a 0%, #020617 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483000,
          color: '#94a3b8',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        Проверка двухфакторной защиты…
      </div>
    );
  }

  if (twoFaOk) {
    return children;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(160deg, #0f172a 0%, #020617 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483000,
        padding: 16,
        boxSizing: 'border-box',
        overflow: 'auto',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          width: 'min(440px, 100%)',
          background: 'linear-gradient(135deg, #1e293b 0%, #020617 100%)',
          borderRadius: 18,
          padding: 24,
          color: '#e5e7eb',
          boxShadow: '0 20px 45px rgba(0,0,0,0.65)',
        }}
      >
        <h3 style={{ margin: '0 0 12px 0', fontSize: 20, fontWeight: 800 }}>
          Обязательная двухфакторная аутентификация
        </h3>
        <p style={{ marginTop: 0, marginBottom: 16, fontSize: 14, color: '#9ca3af', lineHeight: 1.45 }}>
          Для работы в приложении нужно включить 2FA (TOTP). Добавьте аккаунт в Google Authenticator,
          Microsoft Authenticator или аналог и подтвердите кодом. На других устройствах при первом входе
          после истечения сессии потребуется то же самое.
        </p>

        {error && (
          <div style={{ marginBottom: 12, fontSize: 13, color: '#f97373' }}>{error}</div>
        )}

        {setupLoading && !setupSecret && (
          <div style={{ color: '#94a3b8', marginBottom: 12 }}>Подготовка ключа…</div>
        )}

        {setupSecret && (
          <form onSubmit={submitEnable} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TotpSetupQrBlock
              value={setupOtpauth}
              size={240}
              caption="Откройте Google Authenticator → «+» → «Сканировать QR-код» и наведите камеру на код (удобнее с другого устройства)."
            />
            {setupOtpauth && (
              <a href={setupOtpauth} style={{ color: '#38bdf8', fontSize: 14, textAlign: 'center' }}>
                Открыть ссылку otpauth (если Authenticator на этом же телефоне)
              </a>
            )}
            <div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 6 }}>
                Секретный ключ (ручной ввод, если нельзя отсканировать QR):
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 10,
                  alignItems: 'stretch',
                }}
              >
                <code
                  style={{
                    flex: '1 1 180px',
                    display: 'block',
                    padding: 10,
                    borderRadius: 8,
                    background: '#0f172a',
                    color: '#a5f3fc',
                    fontSize: 13,
                    wordBreak: 'break-all',
                    margin: 0,
                    boxSizing: 'border-box',
                    userSelect: 'all',
                    WebkitUserSelect: 'all',
                  }}
                >
                  {setupSecret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  style={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(56,189,248,0.45)',
                    background: 'rgba(56,189,248,0.12)',
                    color: '#7dd3fc',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    minHeight: 44,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <FiCopy size={18} aria-hidden />
                  Копировать ключ
                </button>
              </div>
              {copyHint ? (
                <div style={{ marginTop: 8, fontSize: 13, color: '#4ade80' }}>{copyHint}</div>
              ) : null}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                Код из Google Authenticator
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={enableCode}
                onChange={(e) => setEnableCode(e.target.value)}
                placeholder="000000"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #334155',
                  background: '#020617',
                  color: '#e5e7eb',
                  fontSize: 16,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              type="submit"
              disabled={setupLoading}
              style={{
                padding: '12px 16px',
                borderRadius: 999,
                border: 'none',
                background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)',
                color: '#0b1120',
                cursor: setupLoading ? 'wait' : 'pointer',
                fontWeight: 700,
                fontSize: 15,
                opacity: setupLoading ? 0.7 : 1,
              }}
            >
              Подтвердить и продолжить
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
